import { Admin } from '@apis/index'
import { Actions, store } from '@store/index'
import { Helper } from '@utils/index'
import React, { ReactNode, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Navigate, Outlet, useNavigate } from 'react-router-dom'

interface ProtectedRouteProps {
  allowedRoles?: string[]
  // Optional fine-grained permissions; if provided, user must have at least one
  allowedPermissions?: string[]
  Component?: any
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  allowedRoles = ['admin'],
  allowedPermissions,
  Component,
}) => {
  const { data: user } = useSelector((state: any) => state.user)
  const [loading, setLoading] = useState<boolean>(true)

  const navigate = useNavigate()
  const fetchUser = async () => {
    try {
      const localStorageUser = await Helper.getUser()
      // If no token or id, consider unauthenticated early
      if (
        !localStorageUser?.id ||
        (!localStorageUser?.token && !localStorageUser?.role)
      ) {
        setLoading(false)
        return
      }
      store.dispatch(Actions.set('user', localStorageUser))
      if (!!localStorageUser?.id && !!localStorageUser?.role) {
        // Minimal server verification for admin users
        if (String(localStorageUser?.role).toLowerCase() === 'admin') {
          await Admin.find({
            id: localStorageUser?.id,
          }).catch(() => {})
        }
      }
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    if (!user?.id) {
      fetchUser()
    }
  }, [])
  if (loading) return null
  const normalizedRole = String(user?.role || '').toLowerCase()
  const isRoleAllowed = allowedRoles
    .map((r) => String(r).toLowerCase())
    .includes(normalizedRole)
  const hasPermission = !allowedPermissions
    ? true
    : Array.isArray(user?.permissions)
      ? user.permissions.some((p: string) =>
          allowedPermissions
            .map((ap) => String(ap).toLowerCase())
            .includes(String(p).toLowerCase()),
        )
      : false
  if (!user || !isRoleAllowed || !hasPermission) {
    return <Navigate to="/" replace />
  }
  return (
    <Component>
      <Outlet />
    </Component>
  )
}

export default ProtectedRoute
